// AddGRN.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAsnByAsnNumber } from '../../services/asnAPI/asn';
import { createGRN } from '../../services/GR/grn';
import { toast } from '../../components/AppToast';
import ItemsTable from '../../components/ItemsTable';

const COLUMN_CONFIGS = {
  skuId: { label: 'SKU ID', maxWidth: 150 },
  uom: { label: 'UOM', maxWidth: 100 },
  openQty: { label: 'Open Qty', maxWidth: 120 },
  receivedQty: { label: 'Received Qty', maxWidth: 130 },
  location: { label: 'Location', maxWidth: 150 },
  manufacturingDate: { label: 'Manufacturing Date', maxWidth: 160 },
  expirationDate: { label: 'Expiration Date', maxWidth: 160 }
};

const BASE_COLUMNS = [
  'skuId', 'uom', 'openQty', 'receivedQty', 'location', 'manufacturingDate', 'expirationDate'
];

const FILTER_PLACEHOLDERS = {
  skuId: 'SKU',
  uom: 'UOM',
  openQty: 'Open',
  receivedQty: 'Recv',
  location: 'Loc',
  manufacturingDate: 'Mfg Date',
  expirationDate: 'Exp Date'
};

export default function AddGRN({ onCancel }) {
  const { t } = useTranslation();
  const [asnNumber, setAsnNumber] = useState('');
  const [asnData, setAsnData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [asnError, setAsnError] = useState('');
  const [grnItems, setGrnItems] = useState({});
  const [locations, setLocations] = useState([]);

  // Fetch locations on component mount
  useEffect(() => {
    // TODO: Fetch locations from API
    // For now, using mock data
    setLocations([
      { id: 'STAGE', name: 'Stage' },
      { id: 'DAMAGE', name: 'Damage' },
      { id: 'RETURN', name: 'Return' },
      { id: 'PACK', name: 'Pack' },
      { id: 'PICKTO', name: 'Pick To' },
      { id: 'OTHER', name: 'Other' }
    ]);
  }, []);

  const validateAndFetchASN = async () => {
    if (!asnNumber.trim()) {
      toast.error(t('goodsReceipt.addGrn.errors.asnRequired'));
      return;
    }

    setLoading(true);
    setAsnError('');
    try {
      const data = await fetchAsnByAsnNumber(asnNumber.trim());
      console.log('ASN fetch result:', data);
      // Check if data is valid (has lineItems array)
      if (!data || !data.lineItems || !Array.isArray(data.lineItems) || data.lineItems.length === 0) {
        console.log('ASN not found or invalid, showing error');
        setAsnError(t('goodsReceipt.addGrn.errors.asnNotFound'));
        setAsnData({ lineItems: [] });
        setShowPopup(true);
        setLoading(false);
        return;
      }
      console.log('ASN found, showing popup with data');
      setAsnData(data);
      setShowPopup(true);
    } catch (error) {
      console.error('Error fetching ASN:', error);
      setAsnError(t('goodsReceipt.addGrn.errors.asnNotFound'));
      setAsnData({ lineItems: [] });
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const transformAsnData = (data) => {
    if (!data?.lineItems) return {};

    const map = {};
    data.lineItems.forEach((item, index) => {
      const skuId = item.skuId || item["SKU ID"] || `UNKNOWN-${index}`;
      const key = `SKU::${skuId}`;

      if (!map[key]) {
        map[key] = [];
      }

      map[key].push({
        skuId: item.skuId || item["SKU ID"] || '',
        uom: item.uom || item["UOM"] || 'EA',
        openQty: item.openQty || item["Open Qty"] || 0,
        receivedQty: 0, // User will enter this
        location: '', // User will select this
        manufacturingDate: '', // User will enter this
        expirationDate: '', // User will enter this
        rowIndex: map[key].length
      });
    });

    return map;
  };

  const allColumns = BASE_COLUMNS;

  const translatedColumnConfigs = React.useMemo(() => {
    const cfg = {};
    Object.keys(COLUMN_CONFIGS).forEach((key) => {
      cfg[key] = {
        ...COLUMN_CONFIGS[key],
        label: t(`goodsReceipt.addGrn.columns.${key}`)
      };
    });
    return cfg;
  }, [t]);

  const translatedFilterPlaceholders = React.useMemo(() => {
    const obj = {};
    Object.keys(FILTER_PLACEHOLDERS).forEach((key) => {
      obj[key] = t(`goodsReceipt.addGrn.filters.${key}`);
    });
    return obj;
  }, [t]);

  const headerLabelFormatter = React.useCallback((groupKey, content) => {
    return groupKey.startsWith('SKU::')
      ? `${t("goodsReceipt.addGrn.columns.skuId")}: ${content[0]?.skuId || groupKey.replace('SKU::', '')}`
      : groupKey;
  }, [t]);

  const cellRenderer = React.useCallback((column, row, originalIndex, options) => {
    const cellData = {
      skuId: row.skuId,
      uom: row.uom,
      openQty: row.openQty,
      receivedQty: grnItems[`SKU::${row.skuId}`]?.[row.rowIndex]?.receivedQty ?? row.receivedQty ?? '0',
      location: grnItems[`SKU::${row.skuId}`]?.[row.rowIndex]?.location ?? row.location ?? '',
      manufacturingDate: grnItems[`SKU::${row.skuId}`]?.[row.rowIndex]?.manufacturingDate ?? row.manufacturingDate ?? '',
      expirationDate: grnItems[`SKU::${row.skuId}`]?.[row.rowIndex]?.expirationDate ?? row.expirationDate ?? ''
    };

    if (column === 'location') {
      return (
        <select
          className="form-select form-select-sm"
          value={cellData[column]}
          onChange={(e) => handleCellChange(`SKU::${row.skuId}`, row.rowIndex, column, e.target.value)}
        >
          <option value="">{t('goodsReceipt.addGrn.selectLocation')}</option>
          {locations.map(loc => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      );
    }

    if (column === 'manufacturingDate' || column === 'expirationDate') {
      return (
        <input
          type="date"
          className="form-control form-control-sm"
          value={cellData[column]}
          onChange={(e) => handleCellChange(`SKU::${row.skuId}`, row.rowIndex, column, e.target.value)}
        />
      );
    }

    if (column === 'receivedQty') {
      return (
        <input
          type="number"
          className="form-control form-control-sm"
          value={cellData[column]}
          onChange={(e) => handleCellChange(`SKU::${row.skuId}`, row.rowIndex, column, e.target.value)}
          step="1"
        />
      );
    }

    return cellData[column] || '-';
  }, [grnItems, locations, t]);

  const filterFunction = React.useCallback((row, originalIndex, filters) => {
    return Object.entries(filters).every(([key, filterVal]) => {
      if (!filterVal) return true;
      const val = row[key];
      return String(val ?? '').toLowerCase().includes(filterVal.toLowerCase());
    });
  }, []);

  const handleCellChange = (groupKey, rowIndex, column, newValue) => {
    setGrnItems(prev => {
      const updated = { ...prev };
      if (!updated[groupKey]) updated[groupKey] = [];
      if (!updated[groupKey][rowIndex]) updated[groupKey][rowIndex] = {};

      updated[groupKey][rowIndex][column] = newValue;
      return updated;
    });
  };

  const handleSaveGRN = async () => {
    try {
      // Prepare GRN payload
      const grnPayload = {
        asnNumber: asnNumber,
        items: Object.values(grnItems).flat().map(item => ({
          skuId: item.skuId,
          receivedQty: parseFloat(item.receivedQty) || 0,
          location: item.location || '',
          manufacturingDate: item.manufacturingDate || '',
          expirationDate: item.expirationDate || ''
        }))
      };

      // If no items from ASN, use manual entries
      if (grnPayload.items.length === 0 && asnData?.lineItems?.length === 0) {
        toast.error(t('goodsReceipt.addGrn.errors.noItems'));
        return;
      }

      await createGRN(grnPayload);
      toast.success(t('goodsReceipt.addGrn.success.saved'));
      setShowPopup(false);
      setAsnData(null);
      setAsnNumber('');
      setGrnItems({});
      setAsnError('');
    } catch (error) {
      console.error('Error saving GRN:', error);
      toast.error(t('goodsReceipt.addGrn.errors.saveFailed'));
    }
  };

  const searchResults = React.useMemo(() => {
    if (!asnData) return {};
    return transformAsnData(asnData);
  }, [asnData]);

  return (
    <div>
      <div className="mb-3">
        <h5 className="mb-3">{t('goodsReceipt.addGrn.title')}</h5>
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label">{t('goodsReceipt.addGrn.asnNumber')}</label>
            <input
              type="text"
              className="form-control"
              value={asnNumber}
              onChange={(e) => setAsnNumber(e.target.value)}
              placeholder={t('goodsReceipt.addGrn.asnPlaceholder')}
            />
          </div>
          <div className="col-md-4">
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={validateAndFetchASN}
                disabled={loading}
              >
                {loading ? t('goodsReceipt.addGrn.loading') : t('goodsReceipt.addGrn.validate')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                {t('Back') || 'Back'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for GRN Creation */}
      {showPopup && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('goodsReceipt.addGrn.modalTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPopup(false);
                    setAsnError('');
                  }}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="mb-3">
                  <strong>{t('goodsReceipt.addGrn.asnNumber')}:</strong> {asnNumber}
                </div>

                {asnError && (
                  <div className="alert alert-danger mb-3">
                    <strong>{t('goodsReceipt.addGrn.errors.asnNotFound')}</strong>
                  </div>
                )}

                {Object.keys(searchResults).length === 0 ? (
                  <div className="text-muted">{t('goodsReceipt.addGrn.noItems')}</div>
                ) : (
                  <ItemsTable
                    data={searchResults}
                    columns={allColumns}
                    columnConfigs={translatedColumnConfigs}
                    filterPlaceholders={translatedFilterPlaceholders}
                    cellRenderer={cellRenderer}
                    headerLabelFormatter={headerLabelFormatter}
                    filterFunction={filterFunction}
                    onCellChange={handleCellChange}
                    onRemoveGroup={() => {}}
                    onSelectionChange={() => {}}
                    readOnly={false}
                    autoSelectOnInput={false}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPopup(false);
                    setAsnError('');
                  }}
                >
                  {t('goodsReceipt.addGrn.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveGRN}
                >
                  {t('goodsReceipt.addGrn.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showPopup && <div className="modal-backdrop show"></div>}
    </div>
  );
}